<script lang="ts">
/**
 * The TeamCan component is used to prevent access to buttons and panels that the current user does not have permission
 * to access. For example, if we had a button that created a tinyurl, we can use this component to hide that button
 * if the user does not have permission to create a tiny url
 *
 * USAGE:
 * Wrap the tinyurl component around whatever html you want to show or hide from the user. This component will check
 * to see if either the organization or workspace has the current permission and if found in either, then we will
 * display the text
 * <TeamCan :org="organization" :team="workspace" name="create_tinyurl">
 *   <Button text="Create Tinyurl">
 * </TeamCan>
 *
 * ------------------------------
 * ORG AND TEAM PROPS ARE OPTIONAL
 * ------------------------------
 * In some scenarios, you may not have both an org and a team so both props are optional.
 * <TeamCan :org="organization" name="create_tinyurl">
 *   <Button text="Create Tinyurl">
 * </TeamCan>
 *
 * <TeamCan :team="workspace" name="create_tinyurl">
 *   <Button text="Create Tinyurl">
 * </TeamCan>
 *
 *
 * -----------------------------
 * YOU MUST ADD ONE OR THE OTHER
 * -----------------------------
 *  Note that you must provide either a team or an org or this component assume that you
 *  do not have permission to perform the action.
 *  <TeamCan :name="create_tinyurl">
 *    THIS TEXT WILL NEVER APPEAR BECAUSE THERE IS NO ORG OR TEAM
 *  </TeamCan>
 *
 *  -------------------------------------------------------------------
 *  PERFORMING SPECIAL OPERATIONS INSTEAD OF HIDING THE INNER COMPONENT
 *  -------------------------------------------------------------------
 *  Sometimes you might not want to hide a button and instead just want to disable it if the user does not have
 *  permission. In those cases, you can use the :show prop to force the component to still render when the
 *  user does not have permission. A slot prop called :can containing the current permission will be
 *  provided to you to perform special actions. If the user doesn't have permission, :can will be
 *  false
 *
 *  <TeamCan :org="orgWithoutPermission" :name="create_tinyurl" show v-slot="{can}">
 *    <!-- The button will render but will be disabled -->
 *    <Button text="Create Tinyurl" :disabled="!can">
 *  </TeamCan>
 */
import { computed, defineComponent, toRefs, type PropType } from "vue";
import type { OrganizationData, TeamData } from "../Data/Models/OrgTypes";
import useTeamCan, { type TeamFeature } from "./useTeamCan";

export default defineComponent({
  props: {
    /**
     * The organization that we are currently using
     */
    org: {
      type: Object as PropType<OrganizationData | null>,
      default: null,
    },

    /**
     * The current team/workspace. team is a shorter word so we used that for the prop
     */
    team: {
      type: Object as PropType<TeamData | null>,
      default: null,
    },

    /**
     * The permission that we want the user to have in order to allow access
     * Example: create_tinyurl, delete_tinyurl, use_tags
     */
    name: {
      type: String as PropType<TeamFeature>,
      required: true,
    },

    /**
     * If show is true then we will still display the component if the user does not have permission to
     * access this feature. If show is enabled you can access a slot prop :can inside of the slot
     * to do something special. For example, you might not want to hide a button and instead
     * just disable it, this prop will allow you to do that
     */
    show: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * We return a render function from setup instead of using a template so that we can be more explicit
   * with the logic here. Returning the scoped slot's children (a VNode array) keeps the component
   * renderless - it adds no wrapper element of its own.
   */
  setup(props, { slots }) {
    const teamCan = useTeamCan(toRefs(props));
    const hasPermission = computed(() => teamCan.can(props.name));

    return () => {
      const slot = slots.default?.({ can: hasPermission.value });

      if (hasPermission.value || props.show) {
        return slot;
      }

      return null;
    };
  },
});
</script>
